import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/* Visore a schermo intero condiviso da carrellata e cielo:
   frecce/tastiera, didascalia, data e contatore.
   Se si apre un VIDEO la musica di sottofondo si ferma per
   lasciare spazio al suo audio, e riprende all'uscita. */
export const Lightbox = ({ media, index, onClose, onNav }) => {
  const total = media.length;
  const current = index !== null ? media[index] : null;
  const bgWasPlaying = useRef(false);

  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav((index + 1) % total);
      if (e.key === "ArrowLeft") onNav((index - 1 + total) % total);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, total, onClose, onNav]);

  /* la musica lascia spazio all'audio dei video */
  useEffect(() => {
    const audio = document.getElementById("bg-music");
    if (!audio) return;
    if (current?.type === "video") {
      if (!audio.paused) {
        bgWasPlaying.current = true;
        audio.pause();
      }
    } else if (bgWasPlaying.current) {
      bgWasPlaying.current = false;
      audio.play().catch(() => {});
    }
  }, [current]);

  return createPortal(
    <AnimatePresence>
      {current && (
        <motion.div
          className="lightbox"
          data-lenis-prevent
          data-testid="lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
        >
          <button
            className="lightbox-close"
            onClick={onClose}
            aria-label="Chiudi"
            data-testid="lightbox-close"
          >
            <X size={20} />
          </button>
          {total > 1 && (
            <>
              <button
                className="lightbox-arrow lightbox-arrow--prev"
                onClick={(e) => {
                  e.stopPropagation();
                  onNav((index - 1 + total) % total);
                }}
                aria-label="Foto precedente"
                data-testid="lightbox-prev"
              >
                <ChevronLeft size={26} />
              </button>
              <button
                className="lightbox-arrow lightbox-arrow--next"
                onClick={(e) => {
                  e.stopPropagation();
                  onNav((index + 1) % total);
                }}
                aria-label="Foto successiva"
                data-testid="lightbox-next"
              >
                <ChevronRight size={26} />
              </button>
            </>
          )}
          {current.type === "video" ? (
            <motion.video
              key={current.src}
              src={encodeURI(current.src)}
              poster={current.poster ? encodeURI(current.poster) : undefined}
              controls
              autoPlay
              playsInline
              className="lightbox-video"
              data-testid="lightbox-video"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <motion.img
              key={current.src}
              src={encodeURI(current.src)}
              alt={current.caption || "Foto a schermo intero"}
              className="lightbox-img"
              data-testid="lightbox-img"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          {current.caption && (
            <p className="lightbox-caption" data-testid="lightbox-caption">
              {current.caption}
            </p>
          )}
          {current.date && (
            <p className="lightbox-date" data-testid="lightbox-date">
              {current.date}
            </p>
          )}
          <span className="lightbox-counter" data-testid="lightbox-counter">
            {index + 1} / {total}
          </span>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
